import React, { useState, useEffect } from 'react';
import { rankingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Trophy, Medal, Award, Star, Settings } from 'lucide-react';

const PERIOD_OPTIONS = [
  { value: 'all_time', label: 'Todo el tiempo' },
  { value: 'monthly', label: 'Este mes' },
  { value: 'quarterly', label: 'Este trimestre' },
  { value: 'yearly', label: 'Este año' },
];

export default function RankingPage() {
  const { user } = useAuth();
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState('all_time');
  const [config, setConfig] = useState(null);

  useEffect(() => {
    loadRanking();
    if (user?.role === 'ADMIN') {
      loadConfig();
    }
  }, [periodType, user]);

  const loadRanking = async () => {
    try {
      const res = await rankingAPI.get(periodType);
      setRanking(res.data);
    } catch (error) {
      toast.error('Error al cargar ranking');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await rankingAPI.getConfig();
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading config');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await rankingAPI.updateConfig({ period_type: periodType });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-8 h-8 text-yellow-500" />;
      case 2:
        return <Medal className="w-8 h-8 text-slate-400" />;
      case 3:
        return <Medal className="w-8 h-8 text-amber-700" />;
      default:
        return <span className="w-8 h-8 flex items-center justify-center font-bold text-slate-400">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
      case 3:
        return 'bg-gradient-to-r from-orange-50 to-amber-50 border-amber-200';
      default:
        return 'bg-white border-slate-100';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="ranking-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ranking de Proveedores</h1>
          <p className="text-slate-600">Clasificación basada en puntos acumulados</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user?.role === 'ADMIN' && (
            <Button variant="outline" onClick={handleSaveConfig}>
              <Settings className="w-4 h-4 mr-2" />
              Guardar como default
            </Button>
          )}
        </div>
      </div>

      {/* Top 3 Podium */}
      {ranking.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {/* Second Place */}
          <Card className={`${getRankBg(2)} mt-8`}>
            <CardContent className="pt-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                  <Medal className="w-8 h-8 text-slate-500" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-600">2do</p>
              <p className="font-semibold mt-2">{ranking[1]?.provider_company_name}</p>
              <div className="flex items-center justify-center gap-1 text-amber-600 font-bold mt-2">
                <Star className="w-5 h-5 fill-current" />
                {ranking[1]?.total_points} pts
              </div>
              <p className="text-sm text-slate-500 mt-1">{ranking[1]?.total_badges} insignias</p>
            </CardContent>
          </Card>

          {/* First Place */}
          <Card className={getRankBg(1)}>
            <CardContent className="pt-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Trophy className="w-10 h-10 text-yellow-500" />
                </div>
              </div>
              <p className="text-3xl font-bold text-yellow-600">1ro</p>
              <p className="font-bold text-lg mt-2">{ranking[0]?.provider_company_name}</p>
              <div className="flex items-center justify-center gap-1 text-amber-600 font-bold text-xl mt-2">
                <Star className="w-6 h-6 fill-current" />
                {ranking[0]?.total_points} pts
              </div>
              <p className="text-sm text-slate-500 mt-1">{ranking[0]?.total_badges} insignias</p>
            </CardContent>
          </Card>

          {/* Third Place */}
          <Card className={`${getRankBg(3)} mt-8`}>
            <CardContent className="pt-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                  <Medal className="w-8 h-8 text-amber-700" />
                </div>
              </div>
              <p className="text-2xl font-bold text-amber-700">3ro</p>
              <p className="font-semibold mt-2">{ranking[2]?.provider_company_name}</p>
              <div className="flex items-center justify-center gap-1 text-amber-600 font-bold mt-2">
                <Star className="w-5 h-5 fill-current" />
                {ranking[2]?.total_points} pts
              </div>
              <p className="text-sm text-slate-500 mt-1">{ranking[2]?.total_badges} insignias</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Full Ranking List */}
      <Card>
        <CardHeader>
          <CardTitle>Tabla de Posiciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {ranking.map((entry) => (
              <div
                key={entry.provider_company_id}
                className={`flex items-center justify-between p-4 rounded-lg border ${getRankBg(entry.rank)}`}
                data-testid={`rank-${entry.rank}`}
              >
                <div className="flex items-center gap-4">
                  {getRankIcon(entry.rank)}
                  <div>
                    <p className="font-semibold">{entry.provider_company_name}</p>
                    <div className="flex gap-2 mt-1">
                      {Object.entries(entry.badges_by_category || {}).map(([cat, count]) => (
                        <Badge key={cat} variant="outline" className="text-xs">
                          {cat}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-500">Insignias</p>
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4 text-amber-500" />
                      <span className="font-bold">{entry.total_badges}</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-500">Puntos</p>
                    <div className="flex items-center gap-1 text-amber-600 font-bold text-lg">
                      <Star className="w-5 h-5 fill-current" />
                      {entry.total_points}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {ranking.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay datos en el ranking</p>
              <p className="text-sm">Asigna insignias a los proveedores para verlos aquí</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
